// src/modules/settings/TenantSettingsView.tsx
import { ChevronDown, ChevronUp, Edit2, Save, AlertCircle, TrendingUp, TrendingDown, Monitor } from 'lucide-react';
import {
  DefatomAgentSettings,
  RemediationSettings,
  ComplianceSettings,
  PatchBySeverityConfiguration,
  ReportExpiryMinutes,
  ReportsSettings,
  FeatureListEntry,
} from '../../components/services/TenantSettingsService';
import { getSeverityIcon } from '../../common/Utils/severityHelpers';
import CollapsibleSection from '../../components/CollapsibleSection';
import { getSeverityColor } from '../../common/Utils/color';
import { formatMinutesReference } from './helpers/rangeValidation';
import { DEFAULT_SPLASHTOP_SETTINGS } from './constants/settingsDefaults';
import { TenantGroupOption, toRemoteAccessForm } from './helpers/remoteAccessSettings';
import { RemoteAccessSummary } from './RemoteAccessSettings';

interface TenantSettingsViewProps {
  tenantData: {
    id: string;
    tenantId: string;
    tenantName: string;
    settings: {
      defatomAgent: DefatomAgentSettings;
      remediation: RemediationSettings;
      complaince: ComplianceSettings;
      patchBySeverityConfiguration: PatchBySeverityConfiguration;
      reports: ReportsSettings;
      featureList: FeatureListEntry[] | null;
    };
  };
  onEdit: () => void;
  /** null when the tenant's groups couldn't be loaded. */
  tenantGroups: TenantGroupOption[] | null;
  expandedSections: Record<string, boolean>;
  toggleSection: (section: string) => void;
}

const SettingRow = ({ label, value }: { label: string; value: string | number }) => (
  <div className="group relative flex items-center justify-between py-4 px-5 bg-gradient-to-r from-[#0f0f0f] to-[#1a1a1a] rounded-lg border border-gray-700/50 hover:border-red-500/40 transition-all duration-300 hover:scale-[1.02]">
    {' '}
    <span className="text-gray-400 text-sm font-medium group-hover:text-gray-200 transition-colors">{label}</span>
    <span className="text-white font-semibold px-3 py-1 bg-[#2a2a2a] rounded-md border border-gray-600/50 group-hover:border-red-500/50 transition-all">
      {value ?? 'Not set'}
    </span>
    <div className="absolute inset-0 bg-gradient-to-r from-red-500/0 via-red-500/5 to-red-500/0 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg pointer-events-none" />
  </div>
);

const formatReportLabel = (key: string) =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();

const TenantSettingsView: React.FC<TenantSettingsViewProps> = ({ tenantData, onEdit, tenantGroups, expandedSections, toggleSection }) => {
  return (
    <div className="p-6 text-white">
      {/* Tenant Info */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-xl">
            {' '}
            <AlertCircle size={28} className="text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">Tenant Settings</h2>
            <p className="text-gray-500 text-sm">Manage your tenant configuration</p>
          </div>
        </div>
        <div className="relative overflow-hidden group flex items-center justify-between py-6 px-6 bg-gradient-to-br from-[#1a1a1a] via-[#1f1f1f] to-[#1a1a1a] rounded-2xl border border-gray-600/50 mb-6 hover:border-red-500/40 transition-all duration-300">
          {' '}
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/0 via-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative z-10">
            <p className="text-gray-500 text-xs uppercase tracking-wider font-semibold mb-1">Tenant Name</p>
            <p className="text-white text-xl font-bold">{tenantData.tenantName}</p>
          </div>
          <div className="relative z-10 text-right">
            <p className="text-gray-500 text-xs uppercase tracking-wider font-semibold mb-1">Tenant ID</p>
            <p className="text-gray-300 text-sm font-mono bg-[#0f0f0f] px-3 py-1 rounded-lg border border-gray-700/50">
              {String(tenantData.tenantId).substring(0, 12)}...
            </p>
          </div>
        </div>
      </div>

      {/* Defatom Agent Settings */}
      <CollapsibleSection
        title="Defatom Agent Settings"
        sectionKey="defatomAgent"
        icon={<AlertCircle size={20} />}
        isExpanded={expandedSections.defatomAgent}
        onToggle={() => toggleSection('defatomAgent')}
      >
        <SettingRow
          label="Days for Identifying Disconnected Endpoint"
          value={`${tenantData.settings.defatomAgent?.numberOfDaysForIdentifyingDisconncetedEndpoint ?? 'Not set'} ${tenantData.settings.defatomAgent?.numberOfDaysForIdentifyingDisconncetedEndpoint ? 'days' : ''}`}
        />
        <SettingRow
          label="Max Endpoint Disconnected Threshold"
          value={`${tenantData.settings.defatomAgent?.maxEndpointDisconnectedThresholdInDays ?? 'Not set'} ${tenantData.settings.defatomAgent?.maxEndpointDisconnectedThresholdInDays ? 'days' : ''}`}
        />
        <SettingRow
          label="Threshold for Tagging Agent as Offline"
          value={`${tenantData.settings.defatomAgent?.thresholdInMinutesForTaggingAgentAsOffline ?? 'Not set'} ${tenantData.settings.defatomAgent?.thresholdInMinutesForTaggingAgentAsOffline ? 'minutes' : ''}`}
        />
        <SettingRow
          label="Offline Threshold for Remediation Plan"
          value={`${tenantData.settings.defatomAgent?.thresholdInMinutesForTaggingAgentAsOfflineForRemediationPlan ?? 'Not set'} ${tenantData.settings.defatomAgent?.thresholdInMinutesForTaggingAgentAsOfflineForRemediationPlan ? 'minutes' : ''}`}
        />
        <div className="group relative flex items-center justify-between py-4 px-5 bg-gradient-to-r from-[#0f0f0f] to-[#1a1a1a] rounded-lg border border-gray-700/50 hover:border-red-500/40 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/10 hover:scale-[1.02]">
          <span className="text-gray-400 text-sm font-medium group-hover:text-gray-200 transition-colors">
            Use Vendor Package Repository as Primary
          </span>
          <span
            className={`px-3 py-1 rounded-md border font-semibold text-sm ${
              tenantData.settings.defatomAgent?.useVendorPackageRepoAsPrimary
                ? 'bg-gray-400/10 text-gray-100 border-gray-500/50'
                : 'bg-red-900/30 text-red-400 border-red-500/50'
            }`}
          >
            {tenantData.settings.defatomAgent?.useVendorPackageRepoAsPrimary ? 'Enabled' : 'Disabled'}
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/0 via-red-500/5 to-red-500/0 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg pointer-events-none" />
        </div>
      </CollapsibleSection>

      {/* Remote Access (Splashtop) */}
      <CollapsibleSection
        title="Remote Access"
        sectionKey="remoteAccess"
        icon={<Monitor size={20} />}
        isExpanded={expandedSections.remoteAccess}
        onToggle={() => toggleSection('remoteAccess')}
      >
        <RemoteAccessSummary
          settings={
            tenantData.settings.featureList === null
              ? null
              : toRemoteAccessForm(tenantData.settings.featureList, DEFAULT_SPLASHTOP_SETTINGS)
          }
          tenantGroups={tenantGroups}
        />
      </CollapsibleSection>

      {/* Remediation Settings */}
      <CollapsibleSection
        title="Remediation Settings"
        sectionKey="remediation"
        icon={<Save size={20} />}
        isExpanded={expandedSections.remediation}
        onToggle={() => toggleSection('remediation')}
      >
        <SettingRow
          label="Software Versions Available for Remediation"
          value={tenantData.settings.remediation?.numberOfSoftwareVersionsAvailableForRemediation ?? 'Not set'}
        />
      </CollapsibleSection>

      {/* Compliance Settings */}
      <CollapsibleSection
        title="Compliance Settings"
        sectionKey="compliance"
        icon={<AlertCircle size={20} />}
        isExpanded={expandedSections.compliance}
        onToggle={() => toggleSection('compliance')}
      >
        <SettingRow
          label="Current Previous Duration"
          value={`${tenantData.settings.complaince?.currentPreviousDurationInDays ?? 'Not set'} ${tenantData.settings.complaince?.currentPreviousDurationInDays ? 'days' : ''}`}
        />
        <SettingRow
          label="Non-Compliance Definition"
          value={`${tenantData.settings.complaince?.nonComplainceDefinitionDays ?? 'Not set'} ${tenantData.settings.complaince?.nonComplainceDefinitionDays ? 'days' : ''}`}
        />
      </CollapsibleSection>

      {/* Patch by Severity Configuration */}
      <CollapsibleSection
        title="Patch Severity Configuration"
        sectionKey="patchSeverity"
        icon={<TrendingUp size={20} />}
        isExpanded={expandedSections.patchSeverity}
        onToggle={() => toggleSection('patchSeverity')}
      >
        {tenantData.settings.patchBySeverityConfiguration?.severityCVEMapping &&
        Array.isArray(tenantData.settings.patchBySeverityConfiguration.severityCVEMapping) &&
        tenantData.settings.patchBySeverityConfiguration.severityCVEMapping.length > 0 ? (
          <>
            <div className="mb-6">
              <h4 className="text-base font-semibold text-gray-300 mb-4 flex items-center gap-2">
                <TrendingDown size={18} className="text-red-500" />
                CVE Score Mapping
              </h4>
              <div className="space-y-3">
                {tenantData.settings.patchBySeverityConfiguration.severityCVEMapping.map((mapping, index) => {
                  const severityColor = getSeverityColor(mapping?.severity);
                  const severityIcon = getSeverityIcon(mapping?.severity);

                  return (
                    <div
                      key={index}
                      className="group relative flex items-center gap-4 py-4 px-5 bg-gradient-to-r from-[#0f0f0f] to-[#1a1a1a] rounded-xl border border-gray-700/50 hover:border-gray-500 transition-all duration-300 hover:shadow-lg hover:shadow-gray-500/10 hover:scale-[1.01]"
                    >
                      <div
                        className={`px-4 py-2 rounded-lg border-2 ${severityColor} flex items-center gap-2 shadow-lg font-medium min-w-[140px] justify-center`}
                      >
                        {severityIcon}
                        <span className="text-sm font-bold uppercase tracking-wide">{mapping?.severity || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="w-8 h-0.5 bg-gradient-to-r from-gray-600 to-transparent" />
                        <ChevronDown size={16} style={{ transform: 'rotate(-90deg)' }} />
                        <div className="w-8 h-0.5 bg-gradient-to-r from-transparent to-gray-600" />
                      </div>
                      <span className="inline-flex items-center justify-center px-4 py-2 bg-[#2a2a2a] rounded-lg border border-gray-600/50 text-white font-mono text-sm font-semibold shadow-md group-hover:border-gray-500 transition-all">
                        {mapping?.cveScore || 'Not set'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {tenantData.settings.patchBySeverityConfiguration?.daysRange &&
              Array.isArray(tenantData.settings.patchBySeverityConfiguration.daysRange) &&
              tenantData.settings.patchBySeverityConfiguration.daysRange.length > 0 && (
                <div>
                  <h4 className="text-base font-semibold text-gray-300 mb-4 flex items-center gap-2">
                    <TrendingUp size={18} className="text-red-500" />
                    Days Range Configuration
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {tenantData.settings.patchBySeverityConfiguration.daysRange.map((range, index) => (
                      <div key={index} className="group relative overflow-hidden">
                        <div className="relative z-10 flex flex-col items-center justify-center p-4 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border border-gray-700/50 hover:border-red-500/40 transition-all duration-300 shadow-lg hover:shadow-red-500/10 hover:scale-105">
                          <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Range {index + 1}</span>
                          <span className="text-lg font-bold text-white font-mono">{range || 'Not set'}</span>
                          {range && <span className="text-xs text-gray-400 mt-1">days</span>}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 via-red-500/5 to-red-500/0 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </>
        ) : (
          <div className="p-4 bg-[#0f0f0f] rounded border border-gray-700 text-gray-400 text-center">
            No patch severity configuration available
          </div>
        )}
      </CollapsibleSection>

      {/* Report Expiry Timing */}
      <CollapsibleSection
        title="Report Expiry Timing"
        sectionKey="reportTiming"
        icon={<AlertCircle size={20} />}
        isExpanded={expandedSections.reportTiming}
        onToggle={() => toggleSection('reportTiming')}
      >
        <div className="mb-3 p-2">
          <SettingRow
            label="Report Listing Threshold"
            value={`${tenantData.settings.reports?.ReportListingThresholdInDays ?? 'Not set'} ${tenantData.settings.reports?.ReportListingThresholdInDays ? 'days' : ''}`}
          />
        </div>
        {tenantData.settings.reports?.ReportExpiryMinutes && Object.keys(tenantData.settings.reports.ReportExpiryMinutes).length > 0 ? (
          <div className="space-y-3 p-2">
            {Object.entries(tenantData.settings.reports.ReportExpiryMinutes).map(([reportType, minutes]) => {
              const n = Number(minutes);
              return <SettingRow key={reportType} label={formatReportLabel(reportType)} value={`${formatMinutesReference(n)}`} />;
            })}
          </div>
        ) : (
          <div className="p-4 bg-[#0f0f0f] rounded border border-gray-700 text-gray-400 text-center">
            No report expiry timing configured
          </div>
        )}
      </CollapsibleSection>

      {/* Action Buttons */}
      <div className="flex gap-4 mt-8 sticky bottom-0 bg-gradient-to-t from-[#161617] via-[#161617] to-transparent py-6 border-t border-gray-700/50 backdrop-blur-sm z-10">
        <button
          className="group relative px-8 py-4 bg-gradient-to-r from-[#b31317] to-[#8b0f13] text-white rounded-xl hover:from-[#d41519] hover:to-[#b31317] transition-all duration-300 font-bold flex items-center gap-3 shadow-2xl hover:shadow-red-500/10 hover:scale-105 overflow-hidden"
          onClick={onEdit}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
          <Edit2 size={20} className="relative z-10" />
          <span className="relative z-10">Edit Settings</span>
        </button>
      </div>
    </div>
  );
};

export default TenantSettingsView;
